import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  Marker,
  Polyline,
  InfoWindow,
} from "@react-google-maps/api";
import dayjs from "dayjs";
import Loading from "./Loading";
import Firebase from "../lib/Firebase";

const ServiceMap = ({trip, booking}) => {
  const mapRef = useRef(null);
  const [path, setPath] = useState([]);
  const [routePath, setRoutePath] = useState(null);
  const [stops, setStops] = useState([]);
  const [center, setCenter] = useState({lat: 37.44, lng: -106.59});
  const timestamp = path[path.length - 1]?.timestamp;
  const geocodeTimestamp = dayjs(new Date()).diff(timestamp, "m");
  const [shuttleData] = Firebase.getShuttleGeolocationData(trip?.shuttle.id);

  useEffect(() => {
    if (booking?.pickup_location || booking?.dropoff_location) {
      setStops([booking.pickup_location, booking.dropoff_location]);
    }
  }, [booking.pickup_location, booking.dropoff_location]);

  useEffect(() => {
    if (shuttleData) {
        const { latitude: lat, longitude: lng, timestamp: tmp } = shuttleData;
        setPath((p) => [...p, {lat, lng, timestamp: tmp ? tmp.toDate() : new Date()}]);
    }
  }, [shuttleData]);

  useEffect(() => {
    if (stops.length <= 0) {
      return;
    }

    (async () => {
      const response = await fetch(
        'https://routes.googleapis.com/directions/v2:computeRoutes',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_API_KEY,
            'X-Goog-FieldMask': 'routes.polyline.encodedPolyline',
          },
          body: JSON.stringify({
            origin: { location: { latLng: { latitude: stops[0].latitude, longitude: stops[0].longitude } } },
            destination: { location: { latLng: { latitude: stops[1].latitude, longitude: stops[1].longitude } } },
            travelMode: 'DRIVE',
          }),
        }
      );
      const data = await response.json();
      if (data.routes?.length) {
        const encoded = data.routes[0].polyline.encodedPolyline;
        const decoded = window.google.maps.geometry.encoding.decodePath(encoded);
        setRoutePath(decoded);
      }
    })();

    setCenter({
      lat: stops[0]?.latitude,
      lng: stops[0]?.longitude,
    });
  }, [stops])

  useEffect(() => {
    if (path?.length > 1) {
      const point1 = path[path.length - 1];
      const point2 = path[path.length - 2];
  
      const point1LatLng = new window.google.maps.LatLng(point1.lat, point1.lng);
      const point2LatLng = new window.google.maps.LatLng(point2.lat, point2.lng);
  
      const angle = window.google.maps.geometry.spherical.computeHeading(
        point1LatLng,
        point2LatLng
      );
      const actualAngle = angle + 90;
  
      const markerUrl = process.env.PUBLIC_URL + "images/icon_van2x.png";
      const marker = document.querySelector(`[src="${markerUrl}"]`);
  
      if (marker) {
        // when it hasn't loaded, it's null
        marker.style.transform = `rotate(${actualAngle}deg)`;
        marker.style.transition = "transform 0.5s";
      }
    }
  }, [path]);

  useEffect(() => {
    const bounds = new window.google.maps.LatLngBounds();

    if (path.length > 0) {
      const {lat, lng} = path[path.length - 1];
      const shuttleLocation = new window.google.maps.LatLng(lat, lng);

      bounds.extend(shuttleLocation);
    }

    if (stops.length > 0) {
      stops.map((stop) => {
        const location = new window.google.maps.LatLng(stop.latitude, stop.longitude);
        return bounds.extend(location);
      });
    }

    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.fitBounds(bounds);
      }
    }, 500);
  }, [path, stops]);

  const icon1 = {
    url: process.env.PUBLIC_URL + "images/icon_van2x.png",
    scaledSize: new window.google.maps.Size(40, 40),
    anchor: new window.google.maps.Point(15, 15),
    scale: 0.7
  };
  const pickupIcon = new window.google.maps.MarkerImage(
    process.env.PUBLIC_URL + "images/pickup_pin.png",
    null,
    null,
    null,
    new window.google.maps.Size(48, 48)
  );
  const dropoffIcon = new window.google.maps.MarkerImage(
    process.env.PUBLIC_URL + "images/dropoff_pin.png",
    null,
    null,
    null,
    new window.google.maps.Size(48, 60)
  );

  if (routePath === null) {
    return <Loading />;
  }

  return (
    <>
      <GoogleMap
        onLoad={(map) => { mapRef.current = map; }}
        zoom={stops.length <= 0 ? 5 : 12}
        center={center}
        mapContainerClassName="map-container"
        options={{
          zoomControl: true,
          mapTypeControl: false,
          fullscreenControl: false,
          scaleControl: false,
          streetViewControl: false,
          controlSize: 27,
          trackViewChanges: false
        }}
      >
        <Polyline
          path={routePath}
          options={{
            strokeColor: 'black',
            strokeOpacity: 1,
            strokeWeight: 3,
            clickable: false,
            draggable: false,
            editable: false,
          }}
        />
        {
          stops.map((stop, idx) => (
            <Marker
              key={idx}
              position={{
                lat: stop.latitude,
                lng: stop.longitude
              }}
              // title={stop.type}
              icon={idx === 0 ? pickupIcon : dropoffIcon}
            />
          ))
        }
        { path.length > 0 &&
            <Marker
              icon={icon1}
              position={path[path.length - 1]}
              style={{height: 40, width: 40}}
            />
        }
        { path.length > 0 &&
            <InfoWindow position={path[path.length - 1]}>
              <span style={{color: "#4A525F"}}>
                <div>{trip?.shuttle?.license_plate}</div>
                <div>
                  { geocodeTimestamp > 1 ?
                    `${geocodeTimestamp} minute${geocodeTimestamp > 1? "s" : ""} ago`
                    : "Real-time location"
                  }
                </div>
              </span>
            </InfoWindow>
        }
      </GoogleMap>
    </>
  );
}

export default ServiceMap;